import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTelegram } from '../../context/TelegramContext';
import SettingsSection from './SettingsSection';

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const SwitchLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: #4a6fa1;
`;

const SwitchToggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    
    &:checked + .slider {
      background: linear-gradient(145deg, #6a11cb, #2575fc);
    }
    
    &:checked + .slider:before {
      transform: translateX(24px);
    }
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #e0e5ec;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05),
                inset -2px -2px 4px rgba(255, 255, 255, 0.5);
    border-radius: 24px;
    transition: all 0.3s ease;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background: white;
      border-radius: 50%;
      transition: all 0.3s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
  }
`;

const FormMessage = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  color: ${props => props.isError ? '#e53e3e' : '#38a169'};
  transition: opacity 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
`;

const AutomationSection = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const [isTransferEnabled, setIsTransferEnabled] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false, visible: false });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTransferSetting();
  }, [telegramUserId]);

  const fetchTransferSetting = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://fftg.farodinprojects.org:3000/get_balance_transfer_setting?user_id=${telegramUserId}`);
      const data = await response.json();
      
      if (response.ok) {
        setIsTransferEnabled(data.enabled);
      }
    } catch (error) {
      console.error('Error fetching balance transfer setting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferToggle = async () => {
    const newState = !isTransferEnabled;
    
    // Оптимистичное обновление UI
    setIsTransferEnabled(newState);
    
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/update_balance_transfer_setting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUserId,
          enabled: newState
        })
      });

      const result = await response.json();
      
      if (result.success) {
        const messageKey = newState ? 'balance_transfer_enabled' : 'balance_transfer_disabled';
        setMessage({ 
          text: t(messageKey), 
          isError: false,
          visible: true 
        });
      } else {
        // Возвращаем предыдущее состояние в случае ошибки
        setIsTransferEnabled(!newState);
        throw new Error('Failed to update setting');
      }
    } catch (error) {
      setIsTransferEnabled(!newState); // Возвращаем предыдущее состояние
      setMessage({ 
        text: t('update_error'), 
        isError: true,
        visible: true 
      });
    }
    
    // Скрыть сообщение через 3 секунды
    setTimeout(() => {
      setMessage(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  return (
    <SettingsSection 
      title={t('balance_transfer_title')}
    >
      {isLoading ? (
        <p>{t('loading')}</p>
      ) : (
        <>
          <SwitchContainer>
            <SwitchLabel>{t('balance_transfer_description')}</SwitchLabel>
            <SwitchToggle>
              <input 
                type="checkbox" 
                checked={isTransferEnabled}
                onChange={handleTransferToggle}
              />
              <span className="slider"></span>
            </SwitchToggle>
          </SwitchContainer>
          <FormMessage 
            isError={message.isError} 
            visible={message.visible}
          >
            {message.text}
          </FormMessage>
        </>
      )}
    </SettingsSection>
  );
};

export default AutomationSection;